import React, { useState } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faPhone,
  faSpinner,
  faGraduationCap,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import { useForm } from '../../hooks/useForm';
import { useUser } from '../../contexts/UserContext';
import {
  useFetchAdmins,
  useFetchClaimsManagers,
  useSetCmTraining,
  useDeleteUser,
  useSyncRcNumbers,
} from '../../services/users';
import { formatPhone } from '../operations/leads/leadHelpers';
import PrivateLayout from '../layouts/PrivateLayout';
import Panel from '../common/Panel';
import DataTable from '../common/DataTable';
import Pagination from '../common/Pagination';
import { ucwords } from '../../lib/strings';

import AdminModal from './modals/AdminModal';

const USER_TYPE_LABELS = {
  admin: 'Administrator',
  claims_admin: 'Claims Administrator',
  billing_admin: 'Billing Admin',
  director: 'Director',
  claims_manager: 'Claims Manager',
  onboarding_manager: 'Onboarding Manager',
  onboarding_specialist: 'Onboarding Specialist',
  onboarding_support_specialist: 'Sales Rep',
  referral: 'Referral Partner',
  it_support: 'IT Support',
};

const Admins = () => {
  const { form, onChange } = useForm({ search: '' });
  const [filters, setFilters] = useState({
    page: 1,
    search: undefined,
    sortBy: 'userLastName',
    sortDir: 'ASC',
  });
  const [adminModal, setAdminModal] = useState(false);
  const queryClient = useQueryClient();
  const { hasRole, hasPermission } = useUser();
  // Manage Trainees (Nic 2026-10-30): without Manage Internal Users the page
  // is Claims Managers only, with one action, Mark or Unmark In Training.
  const canManage = hasPermission('admins:manage');
  const traineesOnly = !canManage;

  const admins = useFetchAdmins(
    filters.page,
    filters.search,
    filters.sortBy,
    filters.sortDir,
    canManage,
  );
  const claimsManagers = useFetchClaimsManagers(traineesOnly);
  const isLoading = canManage ? admins.isLoading : claimsManagers.isLoading;
  const data = canManage ? admins.data : null;
  // The trainee view is one short list: searched here, never paged.
  const traineeRows = (claimsManagers.data?.rows || []).filter((r) => {
    const q = String(filters.search || '').trim().toLowerCase();
    if (!q) return true;
    return `${r.userFirstName} ${r.userLastName} ${r.userEmail}`.toLowerCase().includes(q);
  });
  const rows = canManage ? data?.rows || [] : traineeRows;

  const setTraining = useSetCmTraining(
    (saved) => {
      const who = rows.find((r) => r.userId === saved.userId);
      const name = who ? `${who.userFirstName} ${who.userLastName}` : 'They';
      toast.success(
        saved.userInTraining
          ? `${name} is In Training. Clients won't see their phone until it's removed.`
          : `${name} is no longer In Training.`,
      );
      queryClient.invalidateQueries('admins');
      queryClient.invalidateQueries(['claims-managers']);
    },
    { onError: (err) => toast.error(err.message) },
  );
  const isCm = (row) => row.userType === 'claims_manager';
  const trainingActions = [
    {
      label: 'Mark In Training',
      isVisible: (row) => isCm(row) && !row.userInTraining,
      onClick: (row) =>
        setTraining.mutate({ userId: row.userId, inTraining: true }),
    },
    {
      label: 'Unmark In Training',
      isVisible: (row) => isCm(row) && !!row.userInTraining,
      onClick: (row) =>
        setTraining.mutate({ userId: row.userId, inTraining: false }),
    },
  ];

  const deleteUser = useDeleteUser(
    () => {
      toast.success('The admin has been successfully removed.');
      queryClient.invalidateQueries('admins');
    },
    { onError: (err) => toast.error(err.message) },
  );

  const syncRcNumbers = useSyncRcNumbers(
    (summary) => {
      // Lead with matched so a re-run reading "0 updated" still reads as
      // healthy, not failed.
      const parts = [
        `${summary.matched} matched`,
        `${summary.updated} updated`,
      ];
      if (summary.noDirectNumber > 0) {
        parts.push(`${summary.noDirectNumber} without a direct number`);
      }
      if (summary.noRcEntry > 0) {
        parts.push(`${summary.noRcEntry} not found in RingCentral`);
      }
      toast.success(`RingCentral sync complete: ${parts.join(', ')}.`);
      queryClient.invalidateQueries('admins');
    },
    {
      // The axios interceptor maps the API's { msg } onto err.message.
      onError: (err) => toast.error(err.message),
    },
  );

  const onSubmit = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: form.search });
  };

  return (
    <PrivateLayout>
      <Row>
        <Col>
          <Panel>
            <Row>
              <Col>
                <h2>Internal Users</h2>
                {traineesOnly ? (
                  <div className="text-muted small">
                    Claims Managers only. Mark who is In Training; everything
                    else here is for admins.
                  </div>
                ) : null}
              </Col>
              <Col xs={6} sm={5} className="text-end">
                {hasRole('admin') ? (
                  // Admin ROLE only, matching the server gate (requireRole).
                  <Button
                    variant="outline-secondary"
                    className="me-2"
                    onClick={() => syncRcNumbers.mutate()}
                    disabled={syncRcNumbers.isLoading}
                    title="Pull each internal user's direct number from the RingCentral company directory (matched by email). Manually saved numbers are never blanked."
                  >
                    <FontAwesomeIcon
                      icon={syncRcNumbers.isLoading ? faSpinner : faPhone}
                      spin={syncRcNumbers.isLoading}
                    />{' '}
                    Sync from RingCentral
                  </Button>
                ) : null}
                {canManage ? (
                  <Button
                    variant="outline-primary"
                    onClick={() => setAdminModal(true)}
                  >
                    <FontAwesomeIcon icon={faUser} /> Create User
                  </Button>
                ) : null}
              </Col>
              <Col xs={12} sm={4} className="text-end">
                <form onSubmit={onSubmit}>
                  <Form.Control
                    name="search"
                    value={form.search}
                    type="search"
                    placeholder="Search..."
                    onChange={onChange}
                  ></Form.Control>
                </form>
              </Col>
            </Row>
            <br />
            <DataTable
              columns={[
                {
                  header: 'Last Name',
                  key: 'userLastName',
                  sortable: true,
                },
                {
                  header: 'First Name',
                  key: 'userFirstName',
                  sortable: true,
                },
                {
                  header: 'Email',
                  key: 'userEmail',
                  sortable: true,
                },
                {
                  header: 'Direct Phone',
                  key: 'internalUserPhone',
                  render: (row) =>
                    row.internalUserPhone
                      ? formatPhone(row.internalUserPhone)
                      : '',
                },
                {
                  header: 'Role',
                  key: 'userType',
                  sortable: true,
                  // In Training (2026-10-30): a cap after Claims Manager.
                  render: (row) => (
                    <>
                      {USER_TYPE_LABELS[row.userType] || ucwords(row.userType)}
                      {isCm(row) && row.userInTraining ? (
                        <FontAwesomeIcon
                          icon={faGraduationCap}
                          className="ms-1 text-info"
                          title="In Training: clients and messages leave out their phone"
                          aria-label="In Training"
                        />
                      ) : null}
                    </>
                  ),
                },
              ]}
              busy={isLoading}
              actions={
                canManage
                  ? [
                      {
                        label: 'Edit',
                        onClick: (row) => setAdminModal(row),
                      },
                      { divider: true, isVisible: isCm },
                      ...trainingActions,
                      { divider: true },
                      {
                        label: 'Delete',
                        onClick: (row) => {
                          if (
                            window.confirm(
                              'Are you sure you want to delete/deactivate this user?',
                            )
                          ) {
                            deleteUser.mutate(row.userId);
                          }
                        },
                      },
                    ]
                  : trainingActions
              }
              data={rows}
              defaultSort={`${filters.sortBy}:${filters.sortDir}`}
              onSortChange={(newSortBy, newSortDir) => {
                setFilters({
                  ...filters,
                  page: 1,
                  sortBy: newSortBy,
                  sortDir: newSortDir,
                });
              }}
            />

            {canManage && data && data.total > 0 ? (
              <Pagination
                page={filters.page}
                totalRows={data.total}
                rowsPerPage={50}
                onBack={() =>
                  setFilters({ ...filters, page: filters.page - 1 })
                }
                onForward={() =>
                  setFilters({ ...filters, page: filters.page + 1 })
                }
              />
            ) : null}
          </Panel>
        </Col>
      </Row>
      {canManage ? (
        <AdminModal
          show={adminModal !== false}
          toggle={() => {
            setAdminModal(false);
          }}
          onSuccess={() => queryClient.invalidateQueries('admins')}
          user={adminModal}
        />
      ) : null}
    </PrivateLayout>
  );
};

export default Admins;
